import React from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
// Mock data for campaign conversion funnel
const conversionData = [{
  name: 'Views',
  value: 5200,
  color: '#6E9FD9'
}, {
  name: 'Clicks',
  value: 3800,
  color: '#D9984B'
}, {
  name: 'Applied',
  value: 2100,
  color: '#9C7CB6'
}, {
  name: 'Completed',
  value: 1450,
  color: '#7FC29B'
}];
// Mock data for campaign code usage over time (last 30 days)
const generateCodeUsageData = () => {
  return Array.from({
    length: 30
  }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    // Create a pattern with some variability
    let usageCount = 30 + Math.floor(Math.random() * 40);
    // Add a peak in the middle
    if (i > 10 && i < 20) {
      usageCount += 20 + Math.floor(Math.random() * 30);
    }
    return {
      date: date.toISOString().split('T')[0],
      count: usageCount
    };
  });
};
// Mock data for payment volume over time (last 30 days)
const generatePaymentVolumeData = () => {
  return Array.from({
    length: 30
  }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    // Create a pattern with some variability
    let amount = 1500 + Math.floor(Math.random() * 1000);
    // Add a peak in the middle
    if (i > 10 && i < 20) {
      amount += 1000 + Math.floor(Math.random() * 1500);
    }
    return {
      date: date.toISOString().split('T')[0],
      amount: amount
    };
  });
};
// Format date for display
const formatDate = dateStr => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};
// Conversion Funnel Chart
export const ConversionFunnelChart = () => {
  return <div className="h-80" data-id="element-323">
      <ResponsiveContainer width="100%" height="100%" data-id="element-324">
        <BarChart data={conversionData} layout="vertical" margin={{
        top: 20,
        right: 30,
        left: 40,
        bottom: 5
      }} data-id="element-325">
          <CartesianGrid strokeDasharray="3 3" data-id="element-326" />
          <XAxis type="number" data-id="element-327" />
          <YAxis dataKey="name" type="category" data-id="element-328" />
          <Tooltip formatter={value => [`${value.toLocaleString()}`, 'Count']} data-id="element-329" />
          <Legend data-id="element-330" />
          <Bar dataKey="value" name="Users" data-id="element-331">
            {conversionData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-332" />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>;
};
// Conversion Rate Pie Chart
export const ConversionRatePieChart = () => {
  // Calculate conversion rates
  const viewToClick = (conversionData[1].value / conversionData[0].value * 100).toFixed(1);
  const clickToApply = (conversionData[2].value / conversionData[1].value * 100).toFixed(1);
  const applyToComplete = (conversionData[3].value / conversionData[2].value * 100).toFixed(1);
  const overallConversion = (conversionData[3].value / conversionData[0].value * 100).toFixed(1);
  const rateData = [{
    name: 'View → Click',
    value: parseFloat(viewToClick),
    color: '#6E9FD9'
  }, {
    name: 'Click → Apply',
    value: parseFloat(clickToApply),
    color: '#D9984B'
  }, {
    name: 'Apply → Complete',
    value: parseFloat(applyToComplete),
    color: '#9C7CB6'
  }];
  return <div className="h-80" data-id="element-333">
      <ResponsiveContainer width="100%" height="100%" data-id="element-334">
        <PieChart data-id="element-335">
          <Pie data={rateData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
          name,
          percent
        }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-336">
            {rateData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-337" />)}
          </Pie>
          <Tooltip formatter={value => [`${value}%`, 'Conversion Rate']} data-id="element-338" />
          <Legend data-id="element-339" />
        </PieChart>
      </ResponsiveContainer>
      <div className="text-center mt-2 text-sm font-medium text-gray-700" data-id="element-340">
        Overall Conversion: {overallConversion}%
      </div>
    </div>;
};
// Code Usage Time Series Chart
export const CodeUsageTimeSeriesChart = () => {
  const codeUsageData = generateCodeUsageData();
  return <div className="h-80" data-id="element-341">
      <ResponsiveContainer width="100%" height="100%" data-id="element-342">
        <AreaChart data={codeUsageData} margin={{
        top: 10,
        right: 30,
        left: 0,
        bottom: 0
      }} data-id="element-343">
          <CartesianGrid strokeDasharray="3 3" data-id="element-344" />
          <XAxis dataKey="date" tickFormatter={formatDate} interval={4} data-id="element-345" />
          <YAxis data-id="element-346" />
          <Tooltip formatter={value => [value.toLocaleString(), 'Usage Count']} labelFormatter={label => formatDate(label)} data-id="element-347" />
          <Legend data-id="element-348" />
          <Area type="monotone" dataKey="count" stroke="#5DAFBA" fill="#5DAFBA" fillOpacity={0.3} name="Code Usage" data-id="element-349" />
        </AreaChart>
      </ResponsiveContainer>
    </div>;
};
// Payment Volume Time Series Chart
export const PaymentVolumeTimeSeriesChart = ({
  currency = 'THB'
}) => {
  const paymentVolumeData = generatePaymentVolumeData();
  return <div className="h-80" data-id="element-350">
      <ResponsiveContainer width="100%" height="100%" data-id="element-351">
        <AreaChart data={paymentVolumeData} margin={{
        top: 10,
        right: 30,
        left: 0,
        bottom: 0
      }} data-id="element-352">
          <CartesianGrid strokeDasharray="3 3" data-id="element-353" />
          <XAxis dataKey="date" tickFormatter={formatDate} interval={4} data-id="element-354" />
          <YAxis data-id="element-355" />
          <Tooltip formatter={value => [`${currency} ${value.toLocaleString()}`, 'Volume']} labelFormatter={label => formatDate(label)} data-id="element-356" />
          <Legend data-id="element-357" />
          <Area type="monotone" dataKey="amount" stroke="#7FC29B" fill="#7FC29B" fillOpacity={0.3} name="Payment Volume" data-id="element-358" />
        </AreaChart>
      </ResponsiveContainer>
    </div>;
};